import { getTenantRootDomain, getTenantSlugOverride } from "@/config/runtime";

const sanitize = (value?: string | null) =>
  typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined;

type LogoSet = {
  light?: string;
  dark?: string;
  square?: string;
};

export type BrandConfig = {
  /** Public facing brand name */
  name: string;
  /** Shorter name when there is no room for the full name */
  shortName: string;
  /** Optional one-liner to show below the logo */
  slogan?: string;
  logo: LogoSet;
  login?: {
    tagline?: string;
    heroImage?: string;
  };
  colors?: {
    primary?: string;
    secondary?: string;
  };
  support?: {
    email?: string;
    url?: string;
  };
  hideBrandName?: boolean;
};

const DEFAULT_BRAND: BrandConfig = {
  name: "RomDao",
  shortName: "RomDao",
  slogan: "",
  logo: {
    light: "/logo.png",
    dark: "/logo.png",
    square: "/logo.png",
  },
  login: {
    tagline: "Iniciar sesión para continuar",
    heroImage: "/login.jpg",
  },
};

const {
  VITE_BRAND_NAME,
  VITE_BRAND_SHORT_NAME,
  VITE_BRAND_SLOGAN,
  VITE_BRAND_LOGO_LIGHT,
  VITE_BRAND_LOGO_DARK,
  VITE_BRAND_LOGO_SQUARE,
  VITE_BRAND_LOGIN_TAGLINE,
  VITE_BRAND_LOGIN_HERO,
} = import.meta.env;

const brand: BrandConfig = {
  name: sanitize(VITE_BRAND_NAME) ?? DEFAULT_BRAND.name,
  shortName:
    sanitize(VITE_BRAND_SHORT_NAME) ??
    sanitize(VITE_BRAND_NAME) ??
    DEFAULT_BRAND.shortName,
  slogan: sanitize(VITE_BRAND_SLOGAN) ?? DEFAULT_BRAND.slogan,
  logo: {
    light: sanitize(VITE_BRAND_LOGO_LIGHT) ?? DEFAULT_BRAND.logo.light,
    dark:
      sanitize(VITE_BRAND_LOGO_DARK) ??
      sanitize(VITE_BRAND_LOGO_LIGHT) ??
      DEFAULT_BRAND.logo.dark,
    square:
      sanitize(VITE_BRAND_LOGO_SQUARE) ??
      sanitize(VITE_BRAND_LOGO_LIGHT) ??
      DEFAULT_BRAND.logo.square,
  },
  login: {
    tagline: sanitize(VITE_BRAND_LOGIN_TAGLINE) ?? DEFAULT_BRAND.login?.tagline,
    heroImage: sanitize(VITE_BRAND_LOGIN_HERO) ?? DEFAULT_BRAND.login?.heroImage,
  },
};

const PLATFORM_SUBDOMAINS = new Set(["app", "staging", "www"]);
const TENANT_SLUG_RE = /^[a-z0-9-]+$/;

const escapeRegExp = (value: string) =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Resolve the whitelabel tenant for the current host.
 *
 * - `VITE_TENANT_SLUG` forces a tenant (single-tenant custom domains).
 * - Otherwise `<slug>.<VITE_TENANT_ROOT_DOMAIN>` (default `romdao.com`) is
 *   parsed; platform hosts (app/staging/www) are not tenants.
 */
export function getTenantSubdomain(): string | null {
  const forced = getTenantSlugOverride();
  if (forced) return TENANT_SLUG_RE.test(forced) ? forced : null;

  if (typeof window === "undefined") return null;
  const host = (window.location.hostname || "").toLowerCase();
  const rootDomain = getTenantRootDomain();
  const match = host.match(
    new RegExp(`^([a-z0-9-]+)\\.${escapeRegExp(rootDomain)}$`)
  );
  if (!match) return null;
  const sub = match[1];
  if (PLATFORM_SUBDOMAINS.has(sub)) return null;
  return sub;
}

export default brand;
